import React from "react";

//  import config from 'cfg/config';
import queryString from "query-string";
import appConfig from "cfg/config";
import * as API from "rest/API";

import { getCookie, setCookie } from "rest/API";

import { setData2 } from "redux/actions/data.actions";
import { hasRole, orgId as getCurrentOrgId } from "utils/auth";
import {
    addAuditEvent,
    getHotglueSecureToken,
    hasSuccessfulSyncJob
} from "rest/API";

import swal from "@sweetalert/with-react";
import "./alert.css";

import ErrorDetailsBox from "../components/ErrorDetailsBox";
import { AccountingUtils } from "revlock-accounting";

const { config } = require("../../package.json");

let singletonInstances = {};

// Temporary storage for audit events pending successful link
// Key format: "flowId-entityId-orgId"
const pendingAuditEvents = {};

// Helper function to persist pending audit event after successful link
export const persistPendingAuditEvent = (entity, flow_id, orgId) => {
    const entityId = entity.label || flow_id;
    const key = `${flow_id}-${entityId}-${orgId}`;
    const auditEvent = pendingAuditEvents[key];

    if (auditEvent) {
        console.log("Persisting audit event:", key);
        addAuditEvent(auditEvent.orgId, {
            eventType: auditEvent.eventType,
            entityType: auditEvent.entityType,
            entityId: auditEvent.entityId,
            message: auditEvent.message
        });
        delete pendingAuditEvents[key];
        console.log(
            "Cleared audit event: pendingAuditEvents ",
            pendingAuditEvents
        );
    }
};

export function componentToHex(c) {
    const hex = Math.round(c).toString(16);

    return hex.length == 1 ? "0" + hex : hex;
}

export function rgbToHex(r, g, b) {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

export function colorToHex(color) {
    // This color is from the distinct-colors api.
    const r = color._rgb[0];
    const g = color._rgb[1];
    const b = color._rgb[2];

    return rgbToHex(r, g, b);
}

export const compose = (...funcs) =>
    funcs.reduce(
        (a, b) =>
            (...args) =>
                a(b(...args)),
        (arg) => arg
    );

export function singleton(instances) {
    Object.keys(instances).forEach(
        (key) => (singletonInstances[key] = instances[key])
    );
}

export function get(key) {
    return singletonInstances[key];
}

export function backend() {
    return config.backend;
}

export function bind(instance, ...functions) {
    functions.forEach(
        (func) => (instance[func] = instance[func].bind(instance))
    );
}

// Returns if a value is an array
export function isArray(value) {
    return Array.isArray(value);
}

// Returns if a value is a function
export function isFunction(value) {
    return typeof value === "function";
}

// Returns if a value is an object
export function isObject(value) {
    return value && typeof value === "object";
}

// Returns if a value is really a number
export function isNumber(value) {
    return typeof value === "number" && isFinite(value);
}

// Returns if a value is a string
export function isString(value) {
    return typeof value === "string" || value instanceof String;
}

// convert HTML to text.
export function stripHtml(html) {
    if (!html) return "";
    var tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}

export async function getHGPublicApiKey(orgId, hgConfig) {
    if (window.HotGlue) {
        try {
            const response = await getHotglueSecureToken(orgId);
            hgConfig.api_key = response.publicApiKey;

            window.HotGlue.mount(hgConfig);
        } catch (e) {
            console.error(e);
        }
    }
}

// Helper function to compute JSON diff between oldConfig and newConfig
// Handles nested JSON and masks sensitive fields like API keys
const computeConfigDiff = (oldConfig, newConfig) => {
    // List of sensitive fields that should be masked (only show that they changed, not the values)
    const sensitiveFields = [
        "api_key",
        "password",
        "token",
        "secret",
        "key",
        "apikey",
        "apiKey",
        "access_token",
        "accessToken",
        "aws_access_key_id",
        "aws_secret_access_key"
    ];

    // Helper to check if a field name contains sensitive keywords
    const isSensitiveField = (fieldName) => {
        const lowerFieldName = fieldName.toLowerCase();
        return sensitiveFields.some((sensitive) =>
            lowerFieldName.includes(sensitive)
        );
    };

    // Helper to mask sensitive fields in a config object (for initial setup)
    const maskSensitiveFields = (config) => {
        if (!config || typeof config !== "object" || Array.isArray(config)) {
            return config;
        }
        const masked = { ...config };
        Object.keys(masked).forEach((key) => {
            if (isSensitiveField(key)) {
                masked[key] = "********";
            } else if (
                masked[key] &&
                typeof masked[key] === "object" &&
                !Array.isArray(masked[key])
            ) {
                // Recursively mask nested objects
                masked[key] = maskSensitiveFields(masked[key]);
            }
        });
        return masked;
    };

    // Helper to parse JSON string if possible, otherwise return as-is
    const tryParseJSON = (value) => {
        if (typeof value === "string" && value.trim()) {
            try {
                return JSON.parse(value);
            } catch (e) {
                return value;
            }
        }
        return value;
    };

    // Helper to deep compare and build diff
    // Only includes keys that exist in newVal (removes keys that only exist in oldVal)
    const buildDiff = (oldVal, newVal, path = "") => {
        const diff = { new: {}, old: {} };
        let hasChanges = false;

        // Only iterate over keys in newVal - skip keys that only exist in oldVal
        const newKeys = newVal ? Object.keys(newVal) : [];

        for (const key of newKeys) {
            const fullPath = path ? `${path}.${key}` : key;
            const oldValue = oldVal?.[key];
            const newValue = newVal[key];

            // Check if field is sensitive
            if (isSensitiveField(key)) {
                // For sensitive fields, just indicate change without values
                if (oldValue !== newValue) {
                    // Use maskSensitiveFields to mask the value (consistent with initial setup)
                    diff.new[key] = maskSensitiveFields({ [key]: newValue })[
                        key
                    ];
                    if (oldValue !== undefined) {
                        diff.old[key] = maskSensitiveFields({
                            [key]: oldValue
                        })[key];
                    }
                    hasChanges = true;
                }
                continue;
            }

            // Handle nested objects/arrays
            const oldParsed = tryParseJSON(oldValue);
            const newParsed = tryParseJSON(newValue);

            const oldIsObject =
                oldParsed &&
                typeof oldParsed === "object" &&
                !Array.isArray(oldParsed);
            const newIsObject =
                newParsed &&
                typeof newParsed === "object" &&
                !Array.isArray(newParsed);

            if (oldIsObject && newIsObject) {
                // Both are objects, check for removed keys (exist in old but not in new)
                const oldKeys = Object.keys(oldParsed);
                const newKeys = Object.keys(newParsed);
                const removedKeys = oldKeys.filter((k) => !newKeys.includes(k));

                if (removedKeys.length > 0) {
                    // Show only removed keys in diff.old
                    const removedDiff = {};
                    removedKeys.forEach((removedKey) => {
                        removedDiff[removedKey] = oldParsed[removedKey];
                    });
                    diff.old[key] = removedDiff;
                    hasChanges = true;
                }

                // Also check for changed/added keys by recursing
                const nestedDiff = buildDiff(oldParsed, newParsed, fullPath);
                if (Object.keys(nestedDiff.new).length > 0) {
                    diff.new[key] = nestedDiff.new;
                    hasChanges = true;
                }
                // Merge removed keys from nested diff if any
                if (Object.keys(nestedDiff.old).length > 0) {
                    if (!diff.old[key]) {
                        diff.old[key] = {};
                    }
                    Object.assign(diff.old[key], nestedDiff.old);
                    hasChanges = true;
                }
            } else {
                // Simple value comparison
                const oldStr =
                    typeof oldParsed === "object"
                        ? JSON.stringify(oldParsed)
                        : oldParsed;
                const newStr =
                    typeof newParsed === "object"
                        ? JSON.stringify(newParsed)
                        : newParsed;

                if (oldStr !== newStr) {
                    diff.new[key] = newValue;
                    if (oldValue !== undefined) {
                        diff.old[key] = oldValue;
                    }
                    hasChanges = true;
                }
            }
        }

        return hasChanges ? diff : { new: {}, old: {} };
    };

    // If oldConfig is undefined/null, this is initial setup
    if (!oldConfig || Object.keys(oldConfig).length === 0) {
        // Mask sensitive fields in newConfig for initial setup
        const maskedNewConfig = maskSensitiveFields(newConfig || {});
        return { new: maskedNewConfig };
    }

    // Compute diff
    const diff = buildDiff(oldConfig, newConfig);

    // Return format: { new: {...}, old: {...} }
    return diff;
};

// Helper function to check if currency or timezone has changed from one not null value to another
const hasCurrencyOrTimezoneChanged = (oldConfig, newConfig) => {
    const currencyChanged =
        oldConfig?.default_currency &&
        newConfig?.default_currency &&
        oldConfig.default_currency !== newConfig.default_currency;

    const timezoneChanged =
        oldConfig?.timezone &&
        newConfig?.timezone &&
        oldConfig.timezone !== newConfig.timezone;
    return currencyChanged || timezoneChanged;
};

// Validate currency/timezone changes against successful sync jobs
const validateCurrencyTimezoneChange = async (
    orgId,
    oldConfig,
    newConfig,
    availableEntity,
    flowId
) => {
    const errors = [];
    let entityId = availableEntity?.tap || availableEntity?.target || "";

    // Check if currency or timezone has changed from one not null value to another
    if (hasCurrencyOrTimezoneChanged(oldConfig, newConfig)) {
        try {
            let params = {
                flowId: flowId,
                entityId: entityId
            };
            const result = await hasSuccessfulSyncJob(orgId, params);
            console.log("result:", result);
            if (result && result.hasSuccessfulJob) {
                const changedFields = [];
                if (
                    oldConfig?.default_currency &&
                    newConfig?.default_currency &&
                    oldConfig.default_currency !== newConfig.default_currency
                ) {
                    changedFields.push("currency");
                }
                if (
                    oldConfig?.timezone &&
                    newConfig?.timezone &&
                    oldConfig.timezone !== newConfig.timezone
                ) {
                    changedFields.push("timezone");
                }

                errors.push(
                    `Cannot change ${changedFields.join(
                        " or "
                    )} when there are successful sync jobs for this flow. Please contact support.`
                );
            }
        } catch (error) {
            console.error("Error checking for successful sync jobs:", error);
            // If the check fails, we'll allow the validation to proceed
            // to avoid blocking users due to API errors
        }
    }

    return errors;
};

// Prepare audit event data for config changes (to be persisted after successful link)
const prepareConfigChangeAuditEvent = (
    orgId,
    oldConfig,
    newConfig,
    availableEntity,
    flowId
) => {
    try {
        // Determine if this is initial setup based on whether there are common attributes
        // between oldConfig and newConfig (ignoring keys that only exist in oldConfig)
        let isInitialSetup = true;
        if (
            oldConfig &&
            newConfig &&
            Object.keys(oldConfig).length > 0 &&
            Object.keys(newConfig).length > 0
        ) {
            // Check if there are any common keys between oldConfig and newConfig
            const oldKeys = Object.keys(oldConfig);
            const newKeys = Object.keys(newConfig);
            const hasCommonKeys = newKeys.some((key) => oldKeys.includes(key));
            isInitialSetup = !hasCommonKeys;
        }

        // Compute diff
        const diff = computeConfigDiff(oldConfig, newConfig);

        // Skip auditing if there are no actual changes (both new and old are empty) and it's not initial setup
        const hasNewChanges = diff.new && Object.keys(diff.new).length > 0;
        const hasOldChanges = diff.old && Object.keys(diff.old).length > 0;

        // Generate key upfront for cleanup
        const entityId = availableEntity?.label || flowId;
        const key = `${flowId}-${entityId}-${orgId}`;

        if (!isInitialSetup && !hasNewChanges && !hasOldChanges) {
            // Clear any stale pending event before skipping
            if (pendingAuditEvents[key]) {
                console.log(`Clearing stale audit event (no changes): ${key}`);
                delete pendingAuditEvents[key];
            }
            return; // Skip audit event - no changes to track
        }

        const eventType = isInitialSetup
            ? "etl_config_initialized"
            : "etl_config_update";
        let entityType = flowId;
        // If initial setup, message should be blank
        let message = "";
        if (!isInitialSetup) {
            // Remove old property from message if it's empty or doesn't exist
            const messageDiff = { new: diff.new };
            if (diff.old && Object.keys(diff.old).length > 0) {
                messageDiff.old = diff.old;
            }
            message = JSON.stringify(messageDiff);
        }

        // Store pending audit event (will be persisted after successful link)
        pendingAuditEvents[key] = {
            orgId,
            eventType,
            entityType,
            entityId,
            message
        };

        console.log(`Prepared audit event: ${key}`);
    } catch (error) {
        console.error("Error preparing audit event:", error);
    }
};

// Credential form validator - validates BEFORE changes are saved
// Reference: https://docs.hotglue.com/connection-methods/embedded-widget/options/custom-credential-validation
// This function can be used with HotGlue.show() credentialsFormValidator option
export const createCredentialsFormValidator = (orgId) => {
    return async (oldConfig, newConfig, availableEntity, flowId, tenantId) => {
        const errors = [];

        console.log("credentialsFormValidator called:", {
            newConfig,
            oldConfig,
            availableEntity,
            flowId,
            tenantId
        });

        // Validate currency/timezone changes
        const currencyTimezoneErrors = await validateCurrencyTimezoneChange(
            orgId,
            oldConfig,
            newConfig,
            availableEntity,
            flowId
        );
        console.log("currencyTimezoneErrors:", currencyTimezoneErrors);
        errors.push(...currencyTimezoneErrors);

        // Additional validation: Check if any required fields are missing
        // Add your specific validation rules here

        if (errors.length > 0) {
            return {
                success: false,
                errorMessage: errors.join(". ")
            };
        }

        // Prepare audit event (will be persisted after successful link)
        prepareConfigChangeAuditEvent(
            orgId,
            oldConfig,
            newConfig,
            availableEntity,
            flowId
        );

        return { success: true };
    };
};

export async function showHGDialog({
    orgId,
    flows = ["Commissions", "Invoices", "Sales"],
    callback,
    callbackType = "source",
    linkedCallback,
    unlinkedCallback,
    isSubTenantEnabled = false,
    isMultiSourceEnabled = false
}) {
    const tenant_id = orgId;
    console.log("tenant_id:", tenant_id);

    if (window.HotGlue && window.HotGlue.show) {
        const params = {
            multipleSources: isMultiSourceEnabled,
            subTenantsEnabled: isSubTenantEnabled,
            showSubTenantsId: isSubTenantEnabled,
            filter: {
                flowFilter: (flow) => flows.includes(flow.name)
            },
            // Add credential form validator to prevent invalid saves
            credentialsFormValidator: createCredentialsFormValidator(orgId),
            listener: {
                onSourceLinked: (source, flow_id) => {
                    persistPendingAuditEvent(source, flow_id, orgId);

                    if (callbackType == "source" && linkedCallback) {
                        linkedCallback(source, flow_id);
                    }
                },
                onSourceUnlinked: (source, flow_id) => {
                    if (callbackType == "source" && unlinkedCallback) {
                        unlinkedCallback(source, flow_id);
                    }
                },
                onTargetLinked: (target, flow_id) => {
                    persistPendingAuditEvent(target, flow_id, orgId);

                    if (callbackType == "target" && linkedCallback) {
                        linkedCallback(target, flow_id);
                    }
                },
                onTargetUnlinked: (target, flow_id) => {
                    if (callbackType == "target" && unlinkedCallback) {
                        unlinkedCallback(target, flow_id);
                    }
                },
                onStartJob: (tap, flow, userId) => {
                    callback();
                    addAuditEvent(orgId, {
                        eventType: "data_sync_start",
                        entityType: "flow",
                        entityId: flow,
                        message: `User started HotGlue job ${
                            tap ? "with tap id " + tap : ""
                        }`
                    });
                },
                onSourceLinkCanceled: (tap, flow_id) => {
                    addAuditEvent(orgId, {
                        eventType: "data_sync_cancelled",
                        entityType: "flow",
                        entityId: flow_id,
                        message: `HotGlue job cancelled ${
                            tap ? "with tap id" + tap : ""
                        }`
                    });
                },
                onScheduleSave: (
                    oldSchedule,
                    newSchedule,
                    entityId,
                    flowId,
                    tenantId
                ) => {
                    const messageSchedule = {
                        new: newSchedule,
                        old: oldSchedule
                    };
                    console.log("onScheduleSave called:", {
                        oldSchedule,
                        newSchedule,
                        entityId,
                        flowId,
                        tenantId,
                        messageSchedule
                    });
                    addAuditEvent(orgId, {
                        eventType: "etl_schedule_update",
                        entityType: flowId,
                        entityId: entityId,
                        message: JSON.stringify(messageSchedule)
                    });
                }
            }
        };

        if (hasRole("admin")) {
            try {
                const response = await getHotglueSecureToken(orgId);
                const secureJwtToken = response.token;
                if (secureJwtToken) params.jwtToken = secureJwtToken;
            } catch (e) {
                console.log(e);
            }
        }

        window.HotGlue.show(tenant_id, params);
    } else
        swal({
            text: `Our sync back-end is currently unavailable! Try again later.`,
            title: "Error",
            icon: "warning",
            buttons: false,
            timer: 3000
        });
}

export function showImportDialog() {
    setData2("showImportDialog", true);
}

export function showAlert(props) {
    const { details = undefined } = props;
    const swalProps = { ...props };

    if (details && details !== null) {
        swalProps.content = <ErrorDetailsBox details={details} />;
        delete swalProps.details;
    }

    return swal(swalProps);
}

export function showError(exc) {
    const { details = undefined } = exc;

    const swalProps = exc;
    swalProps.icon = swalProps.icon || "error";
    swalProps.title = swalProps.title || "Warning!";
    swalProps.buttons = swalProps.buttons || true;
    swalProps.text = typeof swalProps.text === "function" ? "" : swalProps.text;

    if (details && details !== null) {
        swalProps.content = <ErrorDetailsBox details={details} />;
        delete swalProps.details;
    }

    return swal(swalProps);
}

// Returns the mstrGroupName
export function getMicroStrategyGroupName(clientId, clientName) {
    const orgName = AccountingUtils.isTestId(clientId)
        ? clientName + "_Test"
        : clientName;

    return orgName + "_" + clientId;
}

export function downloadFromS3(s3Key, name) {
    if (!name) {
        name = s3Key.replace(/^.*[\\\/]/, "");
    }

    const downloadURI = (uri, name) => {
        var link = document.createElement("a");
        link.download = name;
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clientId = getCurrentOrgId();
    API.getS3DownloadUrl(clientId, s3Key)
        .then(({ url }) => {
            downloadURI(url, name);
        })
        .catch((err) => {
            console.log(err);
        });
}

const toRow = (record, columns) => {
    let toReturn =
        (record &&
            columns.map((column) => {
                return record[column];
            })) ||
        [];
    return toReturn;
};

const comparators = {
    period: (a, b) => (a == b ? 0 : a < b ? -1 : 1),
    number: (a, b) => (a == b ? 0 : a < b ? -1 : 1),
    string: (a, b) => (a == b ? 0 : a < b ? -1 : 1)
};

const comparator = (columns, sortCols, sortTypes) => {
    let sortColIndexes = sortCols.map((sortCol) => columns.indexOf(sortCol));
    return function (a, b) {
        let toReturn = sortColIndexes.reduce((result, softColIndex, i) => {
            if (result != 0) return result;
            return comparators[sortTypes[i]](a[softColIndex], b[softColIndex]);
        }, 0);
        return toReturn;
    };
};

export function exportFormatData(exportMetadata, header = true) {
    let toReturn = {};

    Object.keys(exportMetadata).forEach((sheet) => (toReturn[sheet] = []));

    Object.keys(exportMetadata).forEach((sheet) => {
        let { columns, sortCols, sortTypes, get } = exportMetadata[sheet];
        let exportData = [];
        let _exportData = get(columns);
        exportData = exportData.concat(
            _exportData.map((record) => toRow(record, columns))
        );

        exportData.sort(comparator(columns, sortCols, sortTypes));
        if (header) exportData.unshift(columns);
        toReturn[sheet] = toReturn[sheet].concat(exportData);
    });

    return toReturn;
}

export const str2id = (str) => str.replace(/\W/g, "_");

export const ensureOrgProfile = async (orgId, context = {}, callback) => {
    let currentServiceProfile = appConfig.serviceProfile;

    let { url, orgProfile } = context;

    // Only runs when running locally
    if (window.location.hostname === "localhost") {
        const simulatedLocalRedirectProfile = getCookie(
            "simulatedLocalRedirectProfile"
        );
        if (simulatedLocalRedirectProfile)
            console.log(
                `simulatedLocalRedirectProfile: ${simulatedLocalRedirectProfile} `
            );
        currentServiceProfile =
            simulatedLocalRedirectProfile || appConfig.serviceProfile;
    }

    if (!orgProfile) {
        orgProfile = await API.getOrgProfile(orgId);
    }

    // It's unknown when backend dont have the org in version lookup index.
    // Should never happen, but I saw some test orgs in production that has this issue.
    if (orgProfile === "unknown" || currentServiceProfile === orgProfile) {
        callback && callback();
        return;
    }

    let currentPath = window.location.pathname;
    let _queryString = window.location.search;

    if (url) {
        // Handle relative URLs by providing a base URL
        // URL constructor requires absolute URL or base URL as second parameter
        let _url;
        try {
            if (url.startsWith("http://") || url.startsWith("https://")) {
                // Absolute URL
                _url = new URL(url);
            } else {
                // Relative URL - ensure it starts with / for proper path resolution
                const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
                // Use window.location.href as base to properly resolve relative URLs
                _url = new URL(normalizedUrl, window.location.href);
            }
            _queryString = _url.search;
            currentPath = _url.pathname;
        } catch (error) {
            console.error("Error parsing URL:", url, error);
            // Fallback to using url as-is if URL parsing fails
            currentPath = url.startsWith("/") ? url : `/${url}`;
            _queryString = "";
        }
    }

    const currentUrlProfile = currentPath.substring(0, 3);

    let redirectLocation =
        currentUrlProfile === "/a/" ||
        currentUrlProfile === "/b/" ||
        currentUrlProfile === "/c/"
            ? `/${currentPath.substring(3)}`
            : currentPath;

    if (_queryString) {
        // cre is queryString was meant for Amplify Auth module. It's already consumed.
        // We get rid of it here.
        let { cre, ...rest } = queryString.parse(_queryString);

        if (Object.keys(rest).length) {
            let qp = queryString.stringify(rest);
            qp = !qp.startsWith("?") ? `?${qp}` : qp;
            redirectLocation += qp;
        }
    }

    console.log(
        `org service profile ${orgProfile} is different from app service profile ${currentServiceProfile}, redirect location ${redirectLocation}`
    );

    const upgradeRedirect = getCookie("upgradeRedirect");

    if (!upgradeRedirect) {
        console.log(`Set cookie upgradeRedirect to ${redirectLocation}`);
        setCookie("upgradeRedirect", redirectLocation, "/", 1);
    }

    setCookie("orgId", `${orgId}`, "/");

    if (window.location.hostname !== "localhost") {
        // Establish the path where we will redirect to.
        const redirectPath = `/${orgProfile}/index.html`;
        console.log(
            `Running in Cloud Deployment. window.location.href = ${redirectPath}`
        );
        window.location.href = redirectPath;
    } else {
        setCookie("simulatedLocalRedirectProfile", orgProfile, "/", 30);

        const redirectPath = `/index.html`;
        console.log(
            `Running in Local deployment. window.location.href = ${redirectPath}`
        );
        window.location.href = redirectPath;
    }
};
